import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useAnalytics } from '@/modules/analytics/public'
import { DEFAULT_FEATURE_FLAGS } from '@/shared/config/featureFlags'
import { useApiRequest } from './api'

interface FeatureFlag {
  key: string
  enabled: boolean
  value?: unknown
  variant?: string
  experiment?: string
}

interface Experiment {
  key: string
  name: string
  variants: ExperimentVariant[]
  traffic: number
  active: boolean
}

interface ExperimentVariant {
  key: string
  name: string
  weight: number
  config?: unknown
}

let feature_flags_singleton: ReturnType<typeof createFeatureFlags> | null = null

/**
 * Feature flags - состояние приложения, а не состояние отдельного компонента.
 * Все consumer-ы должны читать один набор флагов, загруженный app shell.
 */
export function useFeatureFlags() {
  if (feature_flags_singleton === null) {
    feature_flags_singleton = createFeatureFlags()
  }

  return feature_flags_singleton
}

function createFeatureFlags() {
  const analytics = useAnalytics()

  const flags = ref<Record<string, FeatureFlag>>({})
  const experiments = ref<Record<string, Experiment>>({})
  const userVariants = ref<Record<string, string>>({})
  const isInitialized = ref(false)

  const defaultFlags = DEFAULT_FEATURE_FLAGS

  // Default experiments
  const defaultExperiments: Record<string, Experiment> = {
    checkout_flow: {
      key: 'checkout_flow',
      name: 'Checkout Flow Test',
      variants: [
        { key: 'control', name: 'Original', weight: 50 },
        { key: 'simplified', name: 'Simplified', weight: 50 },
      ],
      traffic: 100,
      active: true,
    },
    product_card_layout: {
      key: 'product_card_layout',
      name: 'Product Card Layout',
      variants: [
        { key: 'standard', name: 'Standard Layout', weight: 60 },
        { key: 'compact', name: 'Compact Layout', weight: 40 },
      ],
      traffic: 50,
      active: true,
    },
  }

  const initializeFlags = async () => {
    if (isInitialized.value) return

    try {
      // Try to load from API first
      const api = useApiRequest()

      try {
        const response = await api.makeRequest<{ key: string; enabled: boolean }[]>(
          '/api/feature-flags',
        )
        if (Array.isArray(response)) {
          // Convert API response to FeatureFlag format
          const apiFlags: Record<string, FeatureFlag> = {}
          for (const flag of response) {
            if (!flag?.key) continue
            apiFlags[flag.key] = {
              key: flag.key,
              enabled: Boolean(flag.enabled),
            }
          }
          Object.assign(flags.value, { ...defaultFlags, ...apiFlags })
          // Save to localStorage as backup
          localStorage.setItem('feature_flags', JSON.stringify(flags.value))
        }
      } catch (apiError) {
        console.warn('Failed to load feature flags from API, using localStorage:', apiError)
        // Fallback to localStorage
        const savedFlags = localStorage.getItem('feature_flags')
        if (savedFlags) {
          const parsed = JSON.parse(savedFlags)
          Object.assign(flags.value, { ...defaultFlags, ...parsed })
        } else {
          Object.assign(flags.value, defaultFlags)
        }
      }

      // Load user variants from localStorage
      const savedVariants = localStorage.getItem('user_variants')
      if (savedVariants) {
        userVariants.value = JSON.parse(savedVariants)
      }

      // Initialize experiments
      experiments.value = defaultExperiments

      // Assign user to experiment variants
      assignExperimentVariants()

      // Initialize Analytics based on flag
      const analyticsEnabled = !!flags.value['analytics_enabled']?.enabled
      if (analyticsEnabled) {
        try {
          analytics.grantAnalyticsConsent()
          await analytics.initializeAnalytics()
        } catch (e) {
          console.warn('Analytics initialization failed:', e)
        }
      } else {
        analytics.denyAnalyticsConsent()
      }

      isInitialized.value = true
    } catch (error) {
      console.error('Failed to initialize feature flags:', error)
      // Fallback to defaults
      Object.assign(flags.value, defaultFlags)
      isInitialized.value = true
    }
  }

  const assignExperimentVariants = () => {
    const userId = getUserId()

    Object.values(experiments.value).forEach((experiment) => {
      if (!experiment.active) return

      // Check if user already has variant assigned
      if (userVariants.value[experiment.key]) {
        return
      }

      // Check if user should participate (traffic control)
      const participateHash = hashString(`${userId}_${experiment.key}_participate`)
      const shouldParticipate = participateHash % 100 < experiment.traffic

      if (!shouldParticipate) {
        userVariants.value[experiment.key] = 'control'
        return
      }

      // Assign variant based on weights
      const variantHash = hashString(`${userId}_${experiment.key}_variant`)
      const totalWeight = experiment.variants.reduce((sum, v) => sum + v.weight, 0)
      const randomValue = variantHash % totalWeight

      let currentWeight = 0
      for (const variant of experiment.variants) {
        currentWeight += variant.weight
        if (randomValue < currentWeight) {
          userVariants.value[experiment.key] = variant.key

          // Track experiment assignment
          analytics.trackEvent('experiment_assigned', {
            experiment_key: experiment.key,
            experiment_name: experiment.name,
            variant_key: variant.key,
            variant_name: variant.name,
          })
          break
        }
      }
    })

    // Save to localStorage
    localStorage.setItem('user_variants', JSON.stringify(userVariants.value))
  }

  const getUserId = (): string => {
    // Get from analytics or generate anonymous ID
    let userId = localStorage.getItem('anonymous_user_id')
    if (!userId) {
      userId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem('anonymous_user_id', userId)
    }
    return userId
  }

  const hashString = (str: string): number => {
    let hash = 0
    for (let i = 0; i < (str?.length || 0); i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }

  // Public API
  const isEnabled = (flagKey: string): boolean => {
    const flag = flags.value[flagKey]
    return flag ? flag.enabled : false
  }

  const getFlag = (flagKey: string): FeatureFlag | null => {
    return flags.value[flagKey] || null
  }

  const getFlagValue = (flagKey: string, defaultValue: unknown = null): unknown => {
    const flag = flags.value[flagKey]
    return flag?.value !== undefined ? flag.value : defaultValue
  }

  const getVariant = (experimentKey: string): string => {
    return userVariants.value[experimentKey] || 'control'
  }

  const isVariant = (experimentKey: string, variantKey: string): boolean => {
    return getVariant(experimentKey) === variantKey
  }

  const setFlag = (flagKey: string, enabled: boolean, value?: unknown) => {
    flags.value[flagKey] = {
      key: flagKey,
      enabled,
      value,
    }

    // Save to localStorage
    localStorage.setItem('feature_flags', JSON.stringify(flags.value))

    // Track flag change
    analytics.trackEvent('feature_flag_changed', {
      flag_key: flagKey,
      enabled,
      value,
    })

    // React to analytics flag changes
    if (flagKey === 'analytics_enabled') {
      if (enabled) {
        try {
          analytics.grantAnalyticsConsent()
          analytics.initializeAnalytics().catch(console.error)
        } catch (e) {
          console.warn('Analytics enable failed:', e)
        }
      } else {
        analytics.denyAnalyticsConsent()
      }
    }
  }

  const trackConversion = (experimentKey: string, conversionType: string = 'default') => {
    const variant = getVariant(experimentKey)
    const experiment = experiments.value[experimentKey]

    if (experiment && variant) {
      analytics.trackEvent('experiment_conversion', {
        experiment_key: experimentKey,
        experiment_name: experiment.name,
        variant_key: variant,
        conversion_type: conversionType,
      })
    }
  }

  // Computed helpers
  const activeExperiments = computed(() => {
    return Object.values(experiments.value).filter((exp) => exp.active)
  })

  const enabledFlags = computed(() => {
    return Object.values(flags.value).filter((flag) => flag.enabled)
  })

  // Lifecycle: при вызове вне компонента (например в router.beforeEach) onMounted не привязан - инициализируем сразу
  const instance = getCurrentInstance()
  if (instance) {
    onMounted(() => {
      initializeFlags()
    })
  } else {
    initializeFlags()
  }

  return {
    // State
    flags,
    experiments,
    userVariants,
    isInitialized,

    // Computed
    activeExperiments,
    enabledFlags,

    // Methods
    isEnabled,
    getFlag,
    getFlagValue,
    getVariant,
    isVariant,
    setFlag,
    trackConversion,
    initializeFlags,

    // Admin methods
    assignExperimentVariants,
  }
}
